import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, inject } from '@angular/core';

import { ChatMessage } from 'src/app/cyn/chat-module/types/chat-session.types';
import { AgentExcelFileService, formatExcelFileSize } from '../../../services/agent-excel-file.service';
import { AiChatAlertService } from '../../../services/ai-chat-alert.service';
import { AgentBlockComponent } from '../agent-block.base';

export interface ExcelFileBlockPayload {
    fileId: string;
}

const UNAVAILABLE_FILE_NAME = 'Excel-файл';

/**
 * Блок скачивания Excel-файла, собранного тулзой `create_excel_file`.
 * Файл лежит в хранилище браузера, поэтому блок работает и при перезагрузке истории чата.
 * Если файл не найден, карточка остаётся на месте, но переходит в состояние «недоступен».
 */
@Component({
    selector: 'chat-excel-file-block',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './chat-excel-file-block.component.html',
    styleUrls: ['./chat-excel-file-block.component.scss'],
})
export class ChatExcelFileBlockComponent
    extends AgentBlockComponent<'excel_file', ExcelFileBlockPayload> {
    @Input() public override message!: ChatMessage<'excel_file', ExcelFileBlockPayload>;

    public fileName = UNAVAILABLE_FILE_NAME;
    public meta = '';
    public isAvailable = false;
    public isDownloading = false;

    private readonly excelFileService = inject(AgentExcelFileService);
    private readonly alertService = inject(AiChatAlertService);
    private readonly changeDetector = inject(ChangeDetectorRef);
    private fileId = '';

    /**
     * Блок не скрываем даже при недоступном файле: пользователь должен видеть,
     * что агент обещал файл, и понимать, почему скачать его нельзя.
     *
     * @inheritdoc
     */
    protected override async validateBeforeRender(): Promise<boolean> {
        const fileId = this.message?.payload?.fileId;

        if (typeof fileId !== 'string' || !fileId.trim()) {
            console.warn('Agent excel_file validation failed: missing fileId');
            this.markUnavailable('Файл не был сформирован. Попросите ассистента повторить выгрузку.');
            return true;
        }

        this.fileId = fileId.trim();

        const record = await this.excelFileService.getFile(this.fileId);

        if (!record) {
            console.warn(`Agent excel_file validation failed: file not found, fileId=${this.fileId}`);
            this.markUnavailable('Файл не найден или срок его хранения истёк. Попросите ассистента сформировать его заново.');
            return true;
        }

        this.fileName = record.fileName;
        this.meta = this.buildMeta(record.sheetName, record.rowCount, record.columnCount, record.size);
        this.isAvailable = true;

        return true;
    }

    public async download(): Promise<void> {
        if (this.isDownloading || !this.isAvailable || !this.fileId) {
            return;
        }

        this.isDownloading = true;

        try {
            const ok = await this.excelFileService.downloadFile(this.fileId);

            if (!ok) {
                this.markUnavailable('Файл больше недоступен. Попросите ассистента сформировать его заново.');
            }
        } catch (error) {
            console.error('Failed to download excel file:', error);
            this.alertService.show('Не удалось скачать файл. Попробуйте ещё раз.');
        } finally {
            this.isDownloading = false;
            this.changeDetector.markForCheck();
        }
    }

    private markUnavailable(reason: string): void {
        this.fileName = UNAVAILABLE_FILE_NAME;
        this.meta = reason;
        this.isAvailable = false;
        this.isDownloading = false;
        this.changeDetector.markForCheck();
    }

    private buildMeta(sheetName: string, rowCount: number, columnCount: number, size: number): string {
        const rows = rowCount === 1 ? '1 строка' : `${rowCount} строк`;
        const columns = columnCount === 1 ? '1 колонка' : `${columnCount} колонок`;

        return [`Лист «${sheetName}»`, rows, columns, formatExcelFileSize(size)].join(' · ');
    }
}
